import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useEdition } from '../../contexts/EditionContext';
import FilmHeader from '../headers/Film';
import FilmCarousel from '../lists/FilmCarousel';
import BookingDetails from '../partials/BookingDetails';
import FilmDescription from '../partials/FilmDescription';
import FilmMetadata from '../partials/FilmMetadata';

import styles from '../../../styles/pages/film.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const FilmPage = ({ page, className }) => {
    const currentEdition = useEdition();
    const { handle: currentEditionHandle = null } = currentEdition || {};

    const { film = null } = page || {};
    const { id = null, screenings = null, sections = null, edition = null } = film || {};

    const { id: editionId = null, handle: editionHandle = null } = edition || {};
    const [section = null] = sections || {};
    const { id: sectionId = null, label = null } = section || {};
    const exceptions = [44, 67];
    const isSectionRelated =
        sectionId !== null && exceptions.indexOf(parseInt(sectionId, 10)) === -1;

    const relatedQuery = useMemo(
        () =>
            isSectionRelated
                ? {
                      section: sectionId,
                      except: id,
                      edition: editionId,
                      randomize: true,
                  }
                : {
                      related: film,
                      randomize: true,
                      edition: editionId,
                      // TODO: test this for old films or provide a fallback
                  },
        [film, id, editionId, isSectionRelated],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <FilmHeader item={film} />
            <div className={classNames(styles.section, styles.details)}>
                <div className={classNames(styles.inner, styles.detailsInner)}>
                    <FilmDescription
                        item={film}
                        className={styles.filmDetails}
                        withoutPoster
                        withPartners
                        isDark
                    />
                    {screenings !== null && screenings.length > 0 ? (
                        <BookingDetails
                            className={styles.bookingDetails}
                            events={screenings}
                            isDark
                        />
                    ) : null}
                </div>
            </div>
            <div className={styles.section}>
                <div className={styles.inner}>
                    <FilmMetadata item={film} isDark />
                </div>
            </div>
            <div className={styles.section}>
                <div className={styles.inner}>
                    <FilmCarousel
                        title={
                            <>
                                {isSectionRelated && label !== null ? (
                                    <FormattedMessage
                                        defaultMessage="Also in {section}"
                                        description="Carousel heading"
                                        values={{ section: label }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Other suggestions"
                                        description="Carousel heading"
                                    />
                                )}
                                {editionHandle !== currentEditionHandle ? (
                                    <span>
                                        {' — '}
                                        <FormattedMessage
                                            defaultMessage="Edition {edition}"
                                            description="Carousel heading"
                                            values={{ edition: editionHandle }}
                                        />
                                    </span>
                                ) : null}
                            </>
                        }
                        query={relatedQuery}
                        isDark
                        lazyLoad
                    />
                </div>
            </div>
        </div>
    );
};

FilmPage.propTypes = propTypes;
FilmPage.defaultProps = defaultProps;

export default FilmPage;
